import {
  ParamsOptionsProps,
  useCartService
} from '../../../../services/MVP/useCartService';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import {
  BulkCartResponse,
  CartItemPayload,
  CartOptions,
  ErrorProps,
  MinimumOrderItemProps,
  NewCartItem,
  NewCartOption
} from '../../../../services/MVP/models';
import { deleteCookie } from '../../../../services/MVP/utils';
import { CookiesKey } from '../../../../services/MVP/enum';
import { updateCartItemsQuantity } from '../../../../store/ducks/cart-items-mvp/actions';
import { useShoppingCart } from '../../../../providers/shoppingcart.provider';

const ERRORS_DEFAULT: ErrorProps = {
  date: false,
  destination: false,
  payment: false,
  taxBenefit: false
};

export const useCart = () => {
  const {
    getCartId,
    getCartItems,
    addBulkCartItem,
    getMinimunOrder,
    createNewCart,
    addCartItem,
    deleteAllItems,
    getCartProductsInformation
  } = useCartService();
  const { updateCartState, productsInformation } = useShoppingCart();

  const dispatch = useDispatch();

  const customersState = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const handleUpdateQuantity = (qty: number) => {
    dispatch(updateCartItemsQuantity(qty));
  };

  const cartId = getCartId();

  const deleteCartId = () => {
    handleUpdateQuantity(0);
    deleteCookie(CookiesKey.CART_ID);
    deleteCookie(CookiesKey.EMITTER_ID);
    deleteCookie(CookiesKey.RECEIVER_ID);
  };

  const cartData = async ({
    alreadyItems,
    withoutMinimum,
    minimumExisting
  }: {
    alreadyItems?: NewCartItem[];
    withoutMinimum?: boolean;
    minimumExisting?: MinimumOrderItemProps[] | undefined;
  } = {}): Promise<{
    items: NewCartItem[];
    minimum: MinimumOrderItemProps[] | undefined;
    cartId?: number | string;
    hasError?: boolean;
  }> => {
    if (cartId) {
      const items = alreadyItems ? alreadyItems : await getCartItems();
      if (items && Array.isArray(items)) {
        const productQuantity = items.filter(
          (item) =>
            item.price.creditPrice !== '' ||
            item.price.creditPrice ||
            Number(item.price.creditPrice) > 0
        );
        handleUpdateQuantity(productQuantity.length ?? 0);

        if (items.length === 0) {
          return { items: [], minimum: undefined, cartId };
        }

        const minimun =
          withoutMinimum || minimumExisting
            ? { items: [] }
            : await getMinimunOrder(items);

        if (!withoutMinimum && !minimumExisting) {
          if (!minimun || !minimun.items || minimun.items.length === 0) {
            handleUpdateQuantity(0);
            return { items: [], minimum: undefined, cartId, hasError: true };
          }
        }

        const cartItemsFormatted = items
          .slice(0, 30)
          .map((itm) => ({
            ...itm,
            extension_attributes: {
              ...(itm.extension_attributes ?? {}),
              tax_benefit_gerdau:
                itm?.extension_attributes?.tax_benefit_gerdau ?? 'nenhum'
            }
          }))
          .filter((c) => {
            try {
              if (!c.extension_attributes.price_gerdau) {
                return Number(c.price.creditPrice) > 0;
              }

              const priceGerdau = JSON.parse(
                c.extension_attributes.price_gerdau
              );
              return Number(priceGerdau.creditPrice) > 0;
            } catch {
              return false;
            }
          });

        const hasIssueOnCart =
          cartItemsFormatted.filter((cartItem) => {
            const withoutOptions =
              !cartItem.cartOptions ||
              Object.values(cartItem.cartOptions).length === 0;
            const withoutExtensionsAtributtes =
              !cartItem.extension_attributes ||
              Object.values(cartItem.extension_attributes).length === 0;

            if (withoutOptions || withoutExtensionsAtributtes) {
              return true;
            }
            const cartOptions = cartItem.cartOptions;
            const destination =
              cartItem.extension_attributes.destination_gerdau;
            const measure = cartItem.extension_attributes.measure_unity_gerdau;

            if (!measure || !destination) {
              return true;
            }

            const withoutOptionsEqualSelected = !cartOptions[destination];
            if (withoutOptionsEqualSelected) {
              return true;
            }

            const optionsEqualSelected = cartOptions[
              destination
            ].measureUnits.find((unit) => unit.measure === measure);
            if (!optionsEqualSelected) {
              return true;
            }

            return false;
          }).length > 0;

        if (hasIssueOnCart) {
          handleUpdateQuantity(0);
          return {
            items: [],
            minimum:
              minimumExisting && minimumExisting.length > 0
                ? minimumExisting
                : minimun?.items,
            cartId,
            hasError: hasIssueOnCart
          };
        }

        const cartItems = cartItemsFormatted.map((item) => {
          const extension_attributes = item.extension_attributes;
          const price = item.price;
          const defaultDestination = price.destination;
          const itemDestination = extension_attributes.destination_gerdau;
          const priceDestination = price.destination;
          const selectDestination =
            !itemDestination && priceDestination
              ? priceDestination
              : itemDestination;

          const destination_gerdau = !itemDestination
            ? defaultDestination
            : selectDestination;

          const measure_unity_gerdau =
            extension_attributes.measure_unity_gerdau === 'TO'
              ? 'T'
              : extension_attributes.measure_unity_gerdau;

          const measureUnits = item.cartOptions[
            destination_gerdau
          ].measureUnits.find((unit) => unit.measure === measure_unity_gerdau);

          const multiple = measureUnits?.multiple ?? 1;

          const qty =
            Number((item.qty % Number(multiple)).toFixed(2)) === 0 //Calculo com ToFixed e Number devido a ponto flutuante -Ex.: 20.3 / 0.700
              ? Number((item.qty / Number(multiple)).toFixed(2))
              : Math.ceil(item.qty / Number(multiple));

          const minimunPerMultiple =
            Number(measureUnits?.minimumQuantity) % Number(multiple) === 0 ||
            (Number(measureUnits?.minimumQuantity) === 1 &&
              Number(multiple) < 1)
              ? Number(measureUnits?.minimumQuantity) / Number(multiple)
              : 1;

          const qtyMinimum =
            qty * Number(multiple) <= Number(measureUnits?.minimumQuantity) &&
            !(
              Number(measureUnits?.minimumQuantity) === 1 &&
              Number(multiple) < 1
            )
              ? minimunPerMultiple ?? 1
              : qty;

          return {
            ...item,
            qty: qtyMinimum,
            extension_attributes: {
              ...item.extension_attributes,
              destination_gerdau,
              measure_unity_gerdau
            },
            errors: ERRORS_DEFAULT
          };
        });
        updateCartState(cartItems);
        return {
          items: cartItems,
          minimum:
            minimumExisting && minimumExisting.length > 0
              ? minimumExisting
              : minimun?.items,
          cartId
        };
      }
      handleUpdateQuantity(0);
      return {
        items: [],
        minimum: [],
        cartId,
        ...(!items && { hasError: !items })
      };
    }
    const newCartId = await createNewCart();
    handleUpdateQuantity(0);
    return { items: [], minimum: [], cartId: newCartId?.cartId };
  };

  const addItemToCart = async (
    item?: CartItemPayload,
    quantity?: number,
    params?: ParamsOptionsProps
  ) => {
    if (!item) {
      return {};
    }
    const newItem = await addCartItem(item, quantity, params);
    return newItem ? { ...newItem, errors: ERRORS_DEFAULT } : {};
  };

  const deleteAllCartItems = async () => {
    const deleteResponse = await deleteAllItems({ updateQty: true });

    return {
      delete: deleteResponse
    };
  };

  const addBulkGetCart = async ({
    items,
    withPrice = true,
    withOptions = true,
    itemsToDelete = [],
    withoutMimimum = false,
    complete = false,
    minimumExisting
  }: {
    items: NewCartItem[];
    updateQty?: boolean;
    withPrice?: boolean;
    withOptions?: boolean;
    complete?: boolean;
    itemsToDelete?: { item_id: number }[];
    withoutMimimum?: boolean;
    minimumExisting?: MinimumOrderItemProps[] | undefined;
  }) => {
    if (
      itemsToDelete &&
      Array.isArray(itemsToDelete) &&
      itemsToDelete.length > 0
    ) {
      const deleteResponse = await deleteAllItems({
        ...(items.length === itemsToDelete.length && { updateQty: true })
      });
      if (deleteResponse !== true || typeof deleteResponse === 'object') {
        return {
          bulk: [],
          delete: deleteResponse,
          cart: {
            items,
            minimum: undefined,
            hasError: true
          },
          errors: [] as string[]
        };
      }
    }

    const newItemsToInsert = items.filter((item) => !item.item_id);
    const itemsAlreadyNewItems = newItemsToInsert.filter((item) =>
      items.some((it) => it.item_id && it.sku === item.sku)
    );

    const noNeedOptions =
      newItemsToInsert.length === itemsAlreadyNewItems.length ||
      newItemsToInsert.length === 0 ||
      items.filter((item) => Object.values(item.cartOptions).length > 0)
        .length === items.length;

    const bulkResponse = await addBulkCartItem({
      items,
      withOptions: noNeedOptions ? false : withOptions,
      withPrice,
      complete
    });

    if (!bulkResponse) {
      throw new Error();
    }

    const { items: bulkItems, errors: bulkErrors } =
      bulkResponse as BulkCartResponse;

    const parseCartOptionsFromProductOptions = (
      productOptionsStr: string | undefined
    ): NewCartOption => {
      const cartOptions: NewCartOption = {};
      try {
        const productOptions: Array<{
          destinations: Array<
            CartOptions & { condGroup: string; description: string }
          >;
        }> = JSON.parse(productOptionsStr ?? '[]');
        productOptions.forEach((options) => {
          options.destinations.forEach((dest) => {
            cartOptions[dest.condGroup] = {
              ...dest,
              destination: {
                code: dest.condGroup,
                description: dest.description
              }
            };
          });
        });
      } catch {
        console.error(
          'parseCartOptionsFromProductOptions error useCart.ts:358'
        );
      }
      return cartOptions;
    };

    const itemsWithOptionsInMemory = bulkItems.map((item) => {
      if (!item.cartOptions || Object.values(item.cartOptions).length === 0) {
        const itemWithOptions = items.find(
          (alreadyOptions) => alreadyOptions.sku === item.sku
        );

        const price = item.extension_attributes.price_gerdau
          ? JSON.parse(item.extension_attributes.price_gerdau)
          : item.price;

        const stateCartOptions = itemWithOptions?.cartOptions ?? {};
        const cartOptions =
          Object.values(stateCartOptions).length > 0
            ? stateCartOptions
            : parseCartOptionsFromProductOptions(
                item.extension_attributes.product_options
              );

        return {
          ...item,
          price,
          cartOptions
        };
      }

      return item;
    });

    const itemData = noNeedOptions ? itemsWithOptionsInMemory : bulkItems;

    const itemResponse = await cartData({
      alreadyItems:
        itemData && Array.isArray(itemData) && itemData.length > 0
          ? itemData
          : undefined,
      withoutMinimum: withoutMimimum,
      minimumExisting
    });

    return {
      bulk: itemData,
      delete: !itemData || !itemResponse.hasError,
      cart: itemResponse,
      errors: bulkErrors ?? [],
      ...(complete && { complete: bulkResponse })
    };
  };

  const getProductsInformation = async ({
    items,
    withInformation = []
  }: {
    items: NewCartItem[];
    withInformation?: NewCartItem[];
  }) => {
    if (!customersState.loading && cartId && items.length > 0) {
      const productsList = items.map((item) => item.sku);

      const alreadyItemsWithInformation = withInformation.length > 0;

      if (alreadyItemsWithInformation) {
        const productsInformation = withInformation;

        const cartProducts = items
          .map((item) => {
            const productInformation = productsInformation?.find(
              (product) => product.sku === item.sku
            );

            const currentMeasure =
              item.extension_attributes?.measure_unity_gerdau;

            return {
              ...item,
              ...(currentMeasure === 'TO' && {
                extension_attributes: {
                  ...item.extension_attributes,
                  measure_unity_gerdau: 'T'
                }
              }),
              media_gallery_entries:
                productInformation?.media_gallery_entries ?? [],
              sap_descricao_gpm: productInformation?.sap_descricao_gpm ?? '',
              descricao_gpm: productInformation?.descricao_gpm ?? ''
            };
          })
          .filter(
            (item) =>
              item.sap_descricao_gpm.length > 0 && item.descricao_gpm.length > 0
          );

        if (cartProducts) {
          return cartProducts;
        }
      } else {
        const hasProductsWithoutInformations = productsList.filter(
          (el) => !productsInformation.map((el) => el.sku).includes(Number(el))
        );
        if (hasProductsWithoutInformations.length > 0) {
          const productsInformationResponse = await getCartProductsInformation(
            productsList
          );

          const cartProducts = items
            .map((item) => {
              const productInformation = productsInformationResponse?.find(
                (product) => product.sku === item.sku
              );

              const currentMeasure =
                item.extension_attributes?.measure_unity_gerdau;

              const mediaCategory =
                productInformation?.extension_attributes?.category_links?.map(
                  (el) => ({
                    ...el,
                    file: el.category_image
                  })
                ) ?? [];

              const mediaGallery =
                productInformation?.media_gallery_entries &&
                productInformation?.media_gallery_entries?.length > 0
                  ? productInformation?.media_gallery_entries?.map((el) => ({
                      ...el,
                      file: `/media/catalog/product/${el.file}`
                    }))
                  : mediaCategory;

              return {
                ...item,
                ...(currentMeasure === 'TO' && {
                  extension_attributes: {
                    ...item.extension_attributes,
                    measure_unity_gerdau: 'T'
                  }
                }),
                media_gallery_entries: mediaGallery,
                sap_descricao_gpm:
                  productInformation?.custom_attributes.find(
                    (att) => att.attribute_code === 'sap_descricao_gpm'
                  )?.value ?? '',
                descricao_gpm:
                  productInformation?.custom_attributes.find(
                    (att) => att.attribute_code === 'descricao_gpm'
                  )?.value ?? ''
              };
            })
            .filter(
              (item) =>
                item.sap_descricao_gpm.length > 0 &&
                item.descricao_gpm.length > 0
            );

          if (cartProducts) {
            return cartProducts;
          }
        }

        const cartProducts = items
          .map((item) => {
            const productInformation = productsInformation?.find(
              (product) => product.sku === Number(item.sku)
            );

            const currentMeasure =
              item.extension_attributes?.measure_unity_gerdau;

            return {
              ...item,
              ...(currentMeasure === 'TO' && {
                extension_attributes: {
                  ...item.extension_attributes,
                  measure_unity_gerdau: 'T'
                }
              }),
              media_gallery_entries:
                productInformation?.media_gallery_entries ?? [],
              sap_descricao_gpm: productInformation?.sap_descricao_gpm ?? '',
              descricao_gpm: productInformation?.descricao_gpm ?? ''
            };
          })
          .filter(
            (item) =>
              item.sap_descricao_gpm.length > 0 && item.descricao_gpm.length > 0
          );

        if (cartProducts) {
          return cartProducts;
        }
      }
    }
    return [];
  };

  return {
    cartId,
    deleteCartId,
    cartData,
    getProductsInformation,
    addItemToCart,
    addBulkGetCart,
    deleteAllCartItems
  };
};
